import { useRef, useState, useEffect } from 'react';
import { api, apiError, mediaUrl, uploadImage } from '../../lib/api';
import { ImagePlus, Trash2, Loader2, Images } from 'lucide-react';

export default function ImageField({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [library, setLibrary] = useState(null);

  useEffect(() => { if (library === 'loading') { api.get('/media').then(({ data }) => setLibrary(data)).catch(() => setLibrary([])); } }, [library]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const data = await uploadImage(file);
      onChange(data.url);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-40 h-28 rounded-xl overflow-hidden bg-white border border-[var(--mm-line)] flex items-center justify-center shrink-0">
          {value ? (
            <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" data-testid={`preview-${testid}`} />
          ) : (
            <span className="text-[11px] text-[var(--mm-ink)]/40">No image</span>
          )}
        </div>
        <div className="flex-1 min-w-[220px] space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`upload-${testid}`}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full bg-[var(--mm-purple)] text-white disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {busy ? 'Uploading…' : 'Upload photo'}
            </button>
            <button type="button" onClick={() => setLibrary(l => (l ? null : 'loading'))} data-testid={`library-${testid}`}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-[var(--mm-line)] bg-white hover:border-[var(--mm-purple)]">
              <Images className="w-4 h-4" /> Library
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')} data-testid={`clear-${testid}`}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-[var(--mm-line)] bg-white text-red-500">
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            )}
          </div>
          <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL"
            data-testid={`url-${testid}`}
            className="w-full h-11 px-3 rounded-xl bg-white border border-[var(--mm-line)] text-sm text-[var(--mm-ink)]" />
          <p className="text-[11px] text-[var(--mm-ink)]/45">JPG, PNG, WEBP or GIF up to 8MB. Use real classroom photography.</p>
          {error && <p className="text-xs text-red-600" data-testid={`error-${testid}`}>{error}</p>}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pick} className="hidden" />
      </div>

      {Array.isArray(library) && (
        <div className="rounded-xl border border-[var(--mm-line)] bg-white p-3" data-testid={`library-panel-${testid}`}>
          {library.length === 0 ? (
            <p className="text-xs text-[var(--mm-ink)]/50 py-2">No photos uploaded yet.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-56 overflow-y-auto">
              {library.map((m) => (
                <button key={m.id} type="button" onClick={() => { onChange(m.url); setLibrary(null); }}
                  data-testid={`library-pick-${m.id}`}
                  className="aspect-[4/3] rounded-lg overflow-hidden border border-[var(--mm-line)] hover:border-[var(--mm-purple)]">
                  <img src={mediaUrl(m.url)} alt={m.original_filename || ''} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
